import Note from './Note'

const SONG_SERVER = 'https://numberednotes.com/'
// Song.js -- example usage:
// var twink = new Song('Twinkle');   // create a song
// twink.rawNotes = ["C", "C", "G", "G", "A", "A", "G", "F", "F", "E", "E", "D", "D", "C`"];
// twink.lyrics = ["/4", "/4", "/4", "/4", "/4", "/4", "/2", "/4", "/4", "/4", "/4", "/4", "/4", "/2"];
// twink.adjustTimingAndLyrics(); // creates the formatted version of notes and lyrics with padding to align visually
// document.getElementById("output1").innerHTML = ('<pre>' + song2.formattedLyrics + '</pre>'); // write result into the page
// document.getElementById("output2").innerHTML = ('<pre>' + song2.formattedNotes + '</pre>');
//
class Song {
  constructor({ id, title, filename, filepath }, scoreTracker) {
    this.id = id
    this.title = title
    this.filename = filename
    this.filepath = filepath
    this.rawNotes = []
    this.lyrics = []
    this.currentPosition = 0 // for guidedPlay mode this is the next note to play
    this.tempo = 60 // beats per mlsPerMinute
    this.timeSignature = '4/4' // default to 4/4
    this.notes = [] // the sequence of Note object instances that make up the song
    this.scoreTracker = scoreTracker

    // TODO add key signature ["C", "G", "Eb"] Major x+2457911   3
    this.readFromCsvFile = this.readFromCsvFile.bind(this)
  }

  currentNote() {
    return this.notes[this.currentPosition]
  }

  nextNote() {
    return this.currentPosition < this.notes.length
      ? this.notes[this.currentPosition]
      : {}
  }

  isAtStart() {
    return this.currentPosition === 0
  }

  advance() {
    const nextI = i => (++i < this.notes.length ? i : 0)
    let i = nextI(this.currentPosition)
    while (this.notes[i].isRest || this.notes[i].isTied) {
      i = nextI(i)
    }
    this.currentPosition = i
    return this.notes[i]
  }

  rewind() {
    this.currentPosition = 0
    this.scoreTracker.reset(this.notes.length)
  }

  // calculate the milliseconds per beat based on the song tempo
  // duration in msec/beat = 60 sec/min * 1000 msec/sec / tempo beats/min
  getBeatDuration() {
    return (60 * 1000) / this.tempo // milliseconds per beat--i.e. per quarter note in 4/4 time
  }

  // Get the note value per beat by assuming an integer after "/" in the time signature e.g. "5/8"
  getTimeSignatureDenominator() {
    return parseInt(this.timeSignature.split('/')[1], 10)
  }

  // play the notes of this song starting at 'position', respecting note pitch and duration
  play(position) {
    if (position < this.notes.length) {
      let note = this.notes[position]
      let duration = note.getDurationInMsec(
        this.getBeatDuration(),
        this.getTimeSignatureDenominator()
      )
      // console.log(`playing ${note.raw} for ${duration} msec/`);
      note.startPlay()
      setTimeout(() => {
        note.stopPlay()
        this.play(position + 1)
      }, duration)
    }
  }

  playWav() {
    if (this.wavUrl) {
      if (!this.wavAudio) {
        this.wavAudio = new Audio()
        this.wavAudio.src = this.wavUrl
      }
      this.wavAudio.play()
      //.then(() => this.wavAudio.play());
    }
  }

  readFromCsvFile(url) {
    var thisSong = this
    var path = url
    function dirname(filepath) {
      return ('' + filepath)
        .split('/')
        .slice(0, -1)
        .join('/')
    }
    return fetch(url)
      .then(response => response.text())
      .then(responseText => {
        thisSong.url = new URL(dirname(path), window.location.href)
        thisSong.parseCsv(responseText)
        thisSong.parseRawNotesAndLyrics()
        thisSong.currentPosition = 0 // start playing at the beginning
        return thisSong
      })
  }

  // Process a string that has the entire contents of a csv file and return a javascript Song object with all
  // information from the file saved as object properties. For instance this might be called with this string:
  //   "name,Twinkle,,,,,,\n"
  //   + "title,Twinkle Twinkle Little Star,,,,,,\n"
  //   + "tempo,60,,,,,,\n"
  //   + "notes,A/4,B/2,C/1,D/8,E/16,F/4,G/4\n"
  //   + "lyrics,My,dog,has,fleas,and,ticks,galore."
  // and it will return this object:
  //  {
  //    name: 'Twinkle',
  //    title: 'Twinkle Twinkle Little Star',
  //    tempo: 60,
  //    notes: ['A/4','B/2','C/1','D/8','E/16','F/4','G/4'],
  //    lyrics: ['My','dog','has','fleas','and','ticks','galore.']
  //  }
  parseCsv(csvString) {
    let lines = csvString.split(/\n|\r/) // NOTE: line separators may be RETURN ('\r' or 0x0a) or NEWLINE ('\n' or 0x0d)
    for (const line of lines) {
      const { label, cols } = this.parseCsvRow(line)
      switch (label) {
        case 'notes':
          this.rawNotes = cols
          break
        case 'lyrics':
          this.lyrics = cols
          break
        case 'tempo':
          this.tempo = parseInt(cols[0], 10)
          break
        case 'cover':
          this.coverImageUrl = this.urlFromFileName(cols[0])
          break
        case 'style':
          this.styleUrl = this.urlFromFileName(cols[0])
          break
        case 'time signature':
          this.timeSignature = cols[0].replace(/TS: */, '')
          break
        case 'video':
          this.videoUrl = cols[0]
          break
        case 'wav':
          this.wavUrl = this.urlFromFileName(cols[0])
          break
        case 'pdf':
          this.pdf = cols[0]
          break

        // handle any other label--convert "a,something" into result.a = 'something'
        default:
          this[label] = cols[0]
      }
    }
    return this
  }
  parseCsvRow(row) {
    // this generator returns an iterator for the comma-separated values
    // on the line, escaping commas inside double quotes.
    function* findCells(line) {
      let result = ''
      let inQuote = false
      for (let c of line) {
        if (!inQuote && c === ',') {
          yield result
          result = ''
        } else if (c === '"') {
          inQuote = !inQuote // note quotes are discarded
        } else {
          result += c // all other characters are passed thru unscathed
        }
      }
      if (result.length > 0) yield result
      return null
    }

    // trim trailing blank cells
    const cells = findCells(row.replace(/,+$/, ''))
    return {
      label: cells.next().value,
      cols: [...cells],
    }
  }

  // Run through the rawNotes array creating a new Note instance combining
  // note & lyric into a single object.
  parseRawNotesAndLyrics() {
    this.notes = []
    let start = 0
    while (this.rawNotes[start].match(/^r/i)) {
      start++
    } // strip leading rests
    for (let i = start; i < this.rawNotes.length; i++) {
      const note = new Note(this.rawNotes[i])
      note.lyric = this.lyrics[i]
      this.notes.push(note)
    }
    return this
  }

  urlFromFileName(filename) {
    let url = new URL(this.url.pathname + '/' + filename, this.url)
    return url.href
  }

  pdfUrlFromSongId(notation, level) {
    const nameHyphenated = this.title.replace(/ /g, '-')
    const filename = `${nameHyphenated}-${notation}-${level}-Numbered-Notes-Piano-Sheet-Music.pdf`

    return `${SONG_SERVER}${this.filepath}/${filename}`
  }
}
export default Song
